import React, {
  useState, createRef, useEffect,
} from 'react';
import ReactDom from 'react-dom';
import styled from 'styled-components';
import {
  Flex, Text, Box, Checkbox, Link, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import FormConsumer from '../../collect-info/FormConsumers';
import { Button, SecondaryButton } from '../../styles';
import { STEPS, step1Questions, formLink } from './data';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function BlueNote({ children }) {
  return (
    <Flex
      bg="#EEF5FF"
      borderRadius="4px"
      py={20}
      pl={16}
      pr={24}
      alignItems="flex-start"
    >
      <Box mt="2px">
        <img src="/j/images/employee-onboarding/blue-note.svg" />
      </Box>
      <Flex flex={1} ml={10} fontSize="12px" color="#1E1E1E">
        {children}
      </Flex>
    </Flex>
  );
}

const StyledStateTaxFormInfo = styled(StateTaxFormInfo)`
  margin-bottom: 0px
`;

function StateW4Form({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  version,
  getPdfUrl,
  companyStaff,
  isOnboardingV2,
}) {
  // initialize steps and editing permissions
  const [currentStep, setCurrentStep] = useState(STEPS.step0);

  // exemption status
  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt_from_withholding || !!data.is_exempt_from_withholding);

  // step 2
  const takeStepTwo = () => {
    if (data.two_job === 'true') return true;
    if (data.two_job === 'false') return true;
    return undefined;
  };
  const [willUserTakeStep2, setWillUserTakeStep2] = useState(takeStepTwo());
  const [selectOptionC, setSelectOptionC] = useState(data.two_job === 'true');

  // step 3
  const takeStepThree = () => (
    ((data.qualifying_children_num || data.qualifying_other_num) && true)
      || undefined
  );
  const [willUserTakeStep3, setwillUserTakeStep3] = useState(takeStepThree);

  // step 4 data:
  const [otherIncome, setOtherIncome] = useState(data.other_income || '');
  const [deductions, setDeductions] = useState(data.deductions || '');
  const [extraWithholding, setExtraWithholding] = useState(
    data.extra_withholding || '',
  );

  const [el, setEl] = useState(document.createElement('div'));

  // prefill name
  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  useEffect(() => {
    const root = document.getElementById('js-note-container');
    root.appendChild(el);
    return () => {
      const root = document.getElementById('js-note-container');
      root.removeChild(el);
    };
  }, []);

  const renderStep0Button = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'You cannot complete this step in Preview Mode.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by Employee.';
    }

    return (
      <Flex flexDirection="column">
        <Button
          disabled={viewResultAsManager}
          data-testid="state_tax_start_btn"
          onClick={() => {
            setCurrentStep(STEPS.exemptionStatus);
          }}
        >
          Start
        </Button>
      </Flex>
    );
  };

  const renderStep0 = () => {
    const faqLink = 'https://www.irs.gov/newsroom/faqs-on-the-2020-form-w-4';
    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <Text fontSize="12px" lineHeight="16px" mt={20}>
          You will electronically complete the Form W-4, so that your employer
          can withhold the correct federal income tax from your pay.
        </Text>
        <Text mt={37} fontSize="14px" semibold>
          Before you get started, familiarize yourself with the Form W-4 with
          the following resources:
        </Text>
        <Box mt={16}>
          <Link href="https://www.irs.gov/pub/irs-pdf/fw4.pdf" target="_blank">
            Form W-4 and instructions
          </Link>
        </Box>
        <Box mt={16}>
          <Link href={faqLink} target="_blank">
            Frequently asked questions
          </Link>
        </Box>
        <Box mt={16}>
          <Link
            href="https://www.irs.gov/individuals/tax-withholding-estimator"
            target="_blank"
          >
            IRS Withholding calculator
          </Link>
        </Box>
        <Box mt={60} />
        {renderStep0Button()}
      </Flex>
    );
  };

  const prependEmployee = (questionId) => {
    const prepended = `employee_${questionId}`;
    return prefilledData[prepended];
  };

  const renderExemptionStatusStep = () => {
    const currentYear = parseInt(version, 10);
    const previousYear = parseInt(version, 10) - 1;

    return (
      <Flex column>
        <Text fontSize="22px" bold>
          Exemption from withholding
        </Text>
        <Text fontSize="12px" lineHeight="16px" mt={20}>
          You may claim exemption from withholding for
          {' '}
          {currentYear}
          {' '}
          if you meet
          both of the following conditions:
          <Box mt={20} ml={20}>
            <ul>
              <li>
                You had no federal income tax liability in
                {' '}
                {previousYear}
              </li>
              <li>
                You expect to have no federal income tax liability in
                {' '}
                {currentYear}
              </li>
            </ul>
          </Box>
        </Text>
        <Text mt={37} mb={10} fontSize="14px" semibold>
          Would you like to claim exemption from withholding in
          {' '}
          {previousYear}
          ?
        </Text>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="radio"
              value={!isExemptFromWithholding}
              checked={!isExemptFromWithholding}
              width="auto"
              onChange={(e) => setIsExemptFromWithholding(!isExemptFromWithholding)}
            />
            <Box ml={2} />
            <Text fontSize="12px" lineHeight="16px">
              No, I am not exempt from withholding.
            </Text>
          </Flex>
        </label>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="radio"
              value={isExemptFromWithholding}
              checked={isExemptFromWithholding}
              width="auto"
              onChange={(e) => setIsExemptFromWithholding(!isExemptFromWithholding)}
            />
            <Box ml={2} />
            <Text fontSize="12px" lineHeight="16px">
              Yes, I am exempt from withholding.
            </Text>
          </Flex>
        </label>
        <Flex mt={20}>
          <SecondaryButton
            onClick={() => {
              setCurrentStep(STEPS.step0);
            }}
          >
            Back
          </SecondaryButton>
          <Flex flex={1} column ml={2}>
            <Button
              onClick={() => {
                const res = {
                  is_exempt_from_withholding: isExemptFromWithholding,
                };
                saveFormData(res, () => {
                  setCurrentStep(STEPS.step1);
                });
              }}
              loading={isSubmitting}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      </Flex>
    );
  };

  const renderExemptionStatus = () => (
    <Flex column>
      <Text fontSize="18px" bold>
        Exempt Status
      </Text>
      <Box mt={16} />
      <label>
        <Flex alignItems="center">
          <S.Input
            type="radio"
            checked={!isExemptFromWithholding}
            width="auto"
            disabled
          />
          <Box ml={2} />
          <Text fontSize="12px" lineHeight="16px">
            No, I am not exempt from withholding.
          </Text>
        </Flex>
      </label>
      <label>
        <Flex alignItems="center">
          <S.Input
            type="radio"
            checked={isExemptFromWithholding}
            width="auto"
            disabled
          />
          <Box ml={2} />
          <Text fontSize="12px" lineHeight="16px">
            Yes, I am exempt from withholding.
          </Text>
        </Flex>
      </label>
      {viewResultAsManager
          && isExemptFromWithholding && (
            <Text fontSize="11px" lineHeight="16px" color="red">
              The employee indicated they are exempt from tax withholding. Only
              the necessary fields for Form W-4 were collected.
            </Text>
      )}
      <Box mt={44} />
    </Flex>
  );

  const step1Form = createRef();
  const step1QuestionsWithAnswers = step1Questions.map((question) => {
    const value = data[question.id]
      || prefilledData[question.id]
      || prependEmployee(question.id)
      || '';
    if (
      !data[question.id]
      && (prefilledData[question.id] || prependEmployee(question.id))
    ) {
      question.locked = true;
    }
    const answer = {
      answer: {
        [question.id]: value,
      },
    };
    return {
      ...question,
      answer,
    };
  });
  const [stepOneQuestions, setStepOneQuestions] = useState(
    step1QuestionsWithAnswers,
  );

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
    </div>
  );

  const renderStep1 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Enter personal information
      </Text>
      <Flex column mt={4}>
        {renderStep1Form()}
      </Flex>
      <Flex>
        <SecondaryButton
          onClick={() => {
            setCurrentStep(STEPS.exemptionStatus);
          }}
        >
          Back
        </SecondaryButton>
        <Flex flex={1} column ml={2}>
          <Button
            data-testid="state_tax_next_btn"
            onClick={() => {
              let err = null;
              if (step1Form && step1Form.current) {
                step1Form.current.validate((error) => {
                  if (error) err = error;
                });
              }
              if (err) {

              } else {
                const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                const res = { ...stepOneData };
                saveFormData(res, () => {
                  if (isExemptFromWithholding) {
                    setCurrentStep(STEPS.step5); // If exempted, skip to step5 (review)
                  } else {
                    setCurrentStep(STEPS.step2);
                  }
                  scrollTop();
                });
              }
            }}
            loading={isSubmitting}
          >
            Next
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const step2Form = createRef();
  const renderStep2Form = (isReviewStep) => (
    <Flex column mb={50}>
      <Text black>
        Do
        {' '}
        <strong>only one</strong>
        {' '}
        of the following
      </Text>
      <Flex mt={24}>
        <Text bold color="black">
          (a)
        </Text>
        <Flex ml={2}>
          <Text>
            Use the estimator at
            {' '}
            <a href="https://www.irs.gov/W4App">www.irs.gov/W4App</a>
            {' '}
            for most accurate withholding for this step (and Steps 3-4). If you
            or your spouse have self-employment income, use this option; or
          </Text>
        </Flex>
      </Flex>
      <Flex mt={24}>
        <Text bold color="black">
          (b)
        </Text>
        <Flex ml={2}>
          <Text>
            Use the Multiple Jobs Worksheet on
            {' '}
            <a href={formLink} target="_blank" rel="noreferrer">
              page 3
            </a>
            {' '}
            and enter the result in Step 4(c) below; or
          </Text>
        </Flex>
      </Flex>
      <Flex mt={24}>
        <Text bold color="black">
          (c)
        </Text>
        <Flex ml={2} column>
          <Text>
            If there are only two jobs total, you may check this box. Do the
            same on Form W-4 for the other job. This option is generally more
            accurate than (b) if pay at the lower paying job is more than half
            of the pay at the higher paying job. Otherwise, (b) is more
            accurate
          </Text>
          <Flex alignItems="center" mt={20}>
            <Box ml={1} />
            <label>
              <Checkbox
                id="selectC"
                checked={selectOptionC}
                onChange={(e) => {
                  setSelectOptionC(e.target.checked);
                }}
                disabled={isReviewStep}
              />
              <Text ml={10} display="inline-block">
                Select option (c)
              </Text>
            </label>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep2 = () => (
    <Flex column>
      {/* <Text fontSize={11} semibold color='#0D8CE6'>
          Step 2
        </Text> */}
      <Text fontSize="22px" bold>
        Multiple Jobs or Spouse Works
      </Text>
      <Flex column mt={10}>
        <BlueNote>
          <Box>
            <Text fontSize="12px" bold>
              ONLY complete this step if one of the following applies to you:
            </Text>
            <br />
            <Text fontSize="12px">
              (1) You hold more than one job at a time
            </Text>
            <Text fontSize="12px">
              (2) You are married filing jointly and your spouse also works.
            </Text>
            <br />
            <a
              target="_blank"
              rel="noopener noreferrer"
              style={{
                lineHeight: '20px',
                textDecoration: 'none',
                color: '#3F51B5',
              }}
              href={formLink}
            >
              <Text fontSize="12">
                View Form W-4 page 2 for additional guidance
              </Text>
            </a>
          </Box>
        </BlueNote>
      </Flex>
      <Flex mt={24} mb={40}>
        <Flex alignItems="center" mr={24}>
          <input
            type="radio"
            id="1"
            name="willUserTakingStep2"
            value="false"
            checked={willUserTakeStep2 === false}
            onChange={() => {
              setWillUserTakeStep2(false);
            }}
          />
          <Box ml={1} />
          <label htmlFor="1">Skip this step</label>
        </Flex>
        <Flex alignItems="center">
          <input
            type="radio"
            id="2"
            name="willUserTakingStep2"
            value="true"
            checked={willUserTakeStep2 === true}
            onChange={() => {
              setWillUserTakeStep2(true);
            }}
          />
          <Box ml={1} />
          <label htmlFor="2">Take this step</label>
        </Flex>
      </Flex>
      {willUserTakeStep2 && renderStep2Form()}
      <Flex>
        <SecondaryButton
          onClick={() => {
            setCurrentStep(STEPS.step1);
          }}
        >
          Back
        </SecondaryButton>
        <Flex flex={1} column ml={2}>
          <Button
            onClick={() => {
              let err = null;
              if (step2Form && step2Form.current) {
                step2Form.current.validate((error) => {
                  if (error) err = error;
                });
              }
              if (err) {

              } else {
                const stepTwoData = {
                  two_job: selectOptionC.toString(),
                };
                const data = { ...stepTwoData };
                saveFormData(data, () => {
                  setCurrentStep(STEPS.step3);
                  scrollTop();
                });
              }
            }}
            loading={isSubmitting}
            disabled={willUserTakeStep2 === undefined}
          >
            Next
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const [qualifyingChildrenNum, setQualifyingChildrenNum] = useState(
    data.qualifying_children_num || '',
  );
  const [qualifyingOtherNum, setQualifyingOtherNum] = useState(
    data.qualifying_other_num || '',
  );
  const [qualifyingOtherNumError, setQualifyingOtherNumError] = useState('');
  const [qualifyingChildrenNumError, setQualifyingChildrenNumError] = useState('');

  const renderStep3Form = (isReviewStep) => {
    const childrenDeduction = Number(qualifyingChildrenNum) * 2000;
    const otherDeduction = Number(qualifyingOtherNum) * 500;
    const totalDeduction = childrenDeduction + otherDeduction;

    return (
      <Flex column mb={50}>
        <S.QuestionWrapper mb="24px">
          <Flex>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              width={1}
              mb="7px"
            >
              <S.QuestionLabel isRequired={false}>
                Number of qualifying children under age 17
              </S.QuestionLabel>
            </Flex>
          </Flex>
          <Flex position="relative">
            <Flex>
              <S.Input
                disabled={isReviewStep}
                type="text"
                data-testid="state_tax_qualifying_children_input"
                value={qualifyingChildrenNum}
                width="auto"
                onChange={(e) => {
                  const numberValue = e.target.value.replace(/[^0-9]/g, '');
                  setQualifyingChildrenNum(numberValue);
                  setQualifyingChildrenNumError(undefined);
                }}
              />
            </Flex>
            <Flex
              ml="8px"
              width="132px"
              px="10px"
              alignItems="center"
              bg="#f7f5f5"
              border="1px solid #dee1ec"
              borderRadius="4px"
            >
              <Text color="black">
                $
                {childrenDeduction}
              </Text>
            </Flex>
          </Flex>
          {qualifyingChildrenNumError && (
            <Text color="red">{qualifyingChildrenNumError}</Text>
          )}
        </S.QuestionWrapper>
        <S.QuestionWrapper mb="24px">
          <Flex>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              width={1}
              mb="7px"
            >
              <S.QuestionLabel isRequired={false}>
                Number of other dependents
              </S.QuestionLabel>
            </Flex>
          </Flex>
          <Flex position="relative">
            <Flex>
              <S.Input
                type="text"
                disabled={isReviewStep}
                value={qualifyingOtherNum}
                width="auto"
                onChange={(e) => {
                  const numberValue = e.target.value.replace(/[^0-9]/g, '');
                  setQualifyingOtherNum(numberValue);
                  setQualifyingOtherNumError(undefined);
                }}
              />
            </Flex>
            <Flex
              ml="8px"
              width="132px"
              px="10px"
              alignItems="center"
              bg="#f7f5f5"
              border="1px solid #dee1ec"
              borderRadius="4px"
            >
              <Text color="black">
                $
                {otherDeduction}
              </Text>
            </Flex>
          </Flex>
          {qualifyingOtherNumError && (
            <Text color="red">{qualifyingOtherNumError}</Text>
          )}
        </S.QuestionWrapper>
        <S.QuestionWrapper mb="24px">
          <Flex>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              width={1}
              mb="7px"
            >
              <S.QuestionLabel isRequired={false}>Total amount</S.QuestionLabel>
            </Flex>
          </Flex>
          <Flex position="relative" flexDirection="column">
            <Flex
              width="132px"
              px="10px"
              alignItems="center"
              bg="#f7f5f5"
              border="1px solid #dee1ec"
              borderRadius="4px"
              height="33px"
            >
              <Text color="black">
                $
                {totalDeduction}
              </Text>
            </Flex>
          </Flex>
        </S.QuestionWrapper>
      </Flex>
    );
  };

  const renderStep3 = () => (
    <Flex column>
      {/* <Text fontSize={11} semibold color='#0D8CE6'>
          Step 3
        </Text> */}
      <Text fontSize="22px" bold>
        Claim Dependents
      </Text>
      <Flex column mt={10}>
        <BlueNote>
          <Box>
            <Text fontSize="12px" bold>
              ONLY complete this step if your income will be $200,000 or less
              ($400,000 or less if married filing jointly).
            </Text>
            <Text fontSize="12px">
              Skip this step if you or your spouse already claimed dependents
              when filing a W-4 for another job.
            </Text>
            <br />
            <a
              target="_blank"
              rel="noopener noreferrer"
              style={{
                lineHeight: '20px',
                textDecoration: 'none',
                color: '#3F51B5',
              }}
              href={formLink}
            >
              <Text fontSize="12">
                View Form W-4 page 2 for additional guidance
              </Text>
            </a>
          </Box>
        </BlueNote>
      </Flex>
      <Flex mt={24} mb={40}>
        <Flex alignItems="center" mr={24}>
          <input
            type="radio"
            id="1"
            name="takeThisStep"
            value="false"
            checked={willUserTakeStep3 === false}
            onChange={() => {
              setwillUserTakeStep3(false);
              setQualifyingChildrenNum('');
              setQualifyingOtherNum('');
            }}
          />
          <Box ml={1} />
          <label htmlFor="1">Skip this step</label>
        </Flex>
        <Flex alignItems="center">
          <input
            type="radio"
            id="2"
            name="takeThisStep"
            value="true"
            checked={willUserTakeStep3 === true}
            onChange={() => {
              setwillUserTakeStep3(true);
            }}
          />
          <Box ml={1} />
          <label htmlFor="2">Take this step</label>
        </Flex>
      </Flex>
      {willUserTakeStep3 && renderStep3Form()}
      <Flex>
        <SecondaryButton
          onClick={() => {
            setCurrentStep(STEPS.step2);
          }}
        >
          Back
        </SecondaryButton>
        <Flex flex={1} column ml={2}>
          <Button
            onClick={() => {
              if (qualifyingOtherNum >= 99 || qualifyingChildrenNum >= 99) {
                setQualifyingOtherNumError(
                  qualifyingOtherNum >= 99
                      && 'Enter a value that is less than 99.',
                );
                setQualifyingChildrenNumError(
                  qualifyingChildrenNum >= 99
                      && 'Enter a value that is less than 99.',
                );
                return;
              }
              const stepThreeData = {
                qualifying_children_num: qualifyingChildrenNum.toString(),
                qualifying_other_num: qualifyingOtherNum.toString(),
              };
              const data = { ...stepThreeData };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step4);
                scrollTop();
              });
            }}
            loading={isSubmitting}
            disabled={willUserTakeStep3 === undefined}
          >
            Next
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep4Form = (isReviewStep) => (
    <Flex column>
      <S.QuestionWrapper mb="24px">
        <Flex>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            width={1}
            mb="7px"
          >
            <S.QuestionLabel isRequired={false}>
              Other income (not from jobs). If you want tax withheld for other
              income you expect this year that won’t have withholding, enter
              the amount of other income here. This may include interest,
              dividends, and retirement income. (4a)
            </S.QuestionLabel>
          </Flex>
        </Flex>
        <Flex position="relative">
          <S.Input
            disabled={isReviewStep}
            type="text"
            width="auto"
            value={`$ ${otherIncome}`}
            onChange={(e) => {
              const value = e.target.value.replace('$ ', '');
              const re = /^\d*(\.)?\d?\d?$/;
              const isPositiveDecimal = re.test(value);
              if (isPositiveDecimal) {
                setOtherIncome(value);
              }
            }}
          />
        </Flex>
      </S.QuestionWrapper>
      <S.QuestionWrapper mb="24px">
        <Flex>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            width={1}
            mb="7px"
          >
            <S.QuestionLabel isRequired={false}>
              Deductions. If you expect to claim deductions other than the
              standard deduction and want to reduce your withholding, use the
              Deductions Worksheet on page 3 and enter the result here. (4b)
            </S.QuestionLabel>
          </Flex>
        </Flex>
        <Flex position="relative">
          <S.Input
            disabled={isReviewStep}
            type="text"
            value={`$ ${deductions}`}
            width="auto"
            onChange={(e) => {
              const value = e.target.value.replace('$ ', '');
              const re = /^\d*(\.)?\d?\d?$/;
              const isPositiveDecimal = re.test(value);
              if (isPositiveDecimal) {
                setDeductions(value);
              }
            }}
          />
        </Flex>
      </S.QuestionWrapper>
      <S.QuestionWrapper mb="24px">
        <Flex>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            width={1}
            mb="7px"
          >
            <S.QuestionLabel isRequired={false}>
              Extra withholding. Enter any additional tax you want withheld
              each pay period (4c)
            </S.QuestionLabel>
          </Flex>
        </Flex>
        <Flex position="relative">
          <S.Input
            type="text"
            disabled={isReviewStep}
            value={`$ ${extraWithholding}`}
            width="auto"
            onChange={(e) => {
              const value = e.target.value.replace('$ ', '');
              const re = /^\d*(\.)?\d?\d?$/;
              const isPositiveDecimal = re.test(value);
              if (isPositiveDecimal) {
                setExtraWithholding(value);
              }
            }}
          />
        </Flex>
      </S.QuestionWrapper>
    </Flex>
  );

  const renderStep4 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Other Adjustments (Optional)
      </Text>
      <Flex column mt={10} mb={30}>
        <BlueNote>
          <Box>
            <Text fontSize="12px" bold>
              Only complete this step if it applies to you.
            </Text>
            <Text fontSize="12px">
              Skip this step if you or your spouse already reported other
              adjustments when filing a W-4 for another job.
            </Text>
            <br />
            <a
              target="_blank"
              rel="noopener noreferrer"
              style={{
                lineHeight: '20px',
                textDecoration: 'none',
                color: '#3F51B5',
              }}
              href={formLink}
            >
              <Text fontSize="12">
                View Form W-4 page 2 for additional guidance
              </Text>
            </a>
          </Box>
        </BlueNote>
      </Flex>
      <Flex column mb={50}>
        {renderStep4Form()}
      </Flex>
      <Flex>
        <SecondaryButton
          onClick={() => {
            setCurrentStep(STEPS.step3);
          }}
        >
          Back
        </SecondaryButton>
        <Flex flex={1} column ml={2}>
          <Button
            onClick={() => {
              const stepFourData = {
                other_income: otherIncome,
                deductions,
                extra_withholding: extraWithholding,
              };
              const data = { ...stepFourData };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step5);
                scrollTop();
              });
            }}
            loading={isSubmitting}
          >
            Next
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep5 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StyledStateTaxFormInfo />
      <Flex column mt={33}>
        {renderExemptionStatus()}
        {/* Step1 result */}
        <Text fontSize="18px" bold>
          Personal information
        </Text>
        <Box mt={16} />
        {renderStep1Form(true)}
        {/* TODO: Conditional render Step 2-4 only if it did not exempt withholding */}
        {!isExemptFromWithholding && (
        <Box>
          {/* step2 result */}
          <Flex mt={44} column>
            <Text fontSize="18px" bold>
              Multiple Jobs or Spouse Works
            </Text>
            <Box mt={16} />
            {renderStep2Form(true)}
          </Flex>

          {/* step3 result */}
          <Flex mt={44} column>
            <Text fontSize="18px" bold>
              Claim Dependents
            </Text>
            <Box mt={16} />
            {renderStep3Form(true)}
          </Flex>

          {/* step4 result */}
          <Flex mt={44} column>
            <Text fontSize="18px" bold>
              Other Adjustments
            </Text>
            <Box mt={16} />
            {renderStep4Form(true)}
          </Flex>
        </Box>
        )}
        {viewResultAsManager
          && companyStaff.pii_permission === 'full_access' && (
            <Flex column>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
        )}
        <Flex mt={50}>
          <SecondaryButton
            onClick={() => {
              if (isExemptFromWithholding) {
              // TODO: If the user is exempted, pressing back will bring them back to Step 1
                setCurrentStep(STEPS.step1);
              } else {
              // TODO: If the user is NOT exempted, presisng back will bring them back to Step 4
                setCurrentStep(STEPS.step4);
              }
            }}
          >
            Back
          </SecondaryButton>
          <Flex flex={1} column ml={2}>
            <Button
              disabled={viewResultAsManager}
              data-testid="state_tax_finalize_and_sign_btn"
              onClick={() => {
                handleSigning();
                setCurrentStep(STEPS.step6);
                scrollTop();
              }}
              loading={isSubmitting}
            >
              Finalize and Sign
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep6 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Sign Form W-4
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );

  const renderHeader = () => {
    const W4PDFLink = (
      <a href="https://www.irs.gov/pub/irs-pdf/fw4.pdf" target="_blank" rel="noreferrer">
        Federal Form W-4
      </a>
    );
    if (
      currentStep === STEPS.step0
      || currentStep === STEPS.step1
      || currentStep === STEPS.step5
      || currentStep === STEPS.step6
    ) return null;
    let noteComponent;
    if (currentStep === STEPS.step2) {
      noteComponent = (
        <>
          <Text bold color="#000">
            Complete Step 2-4 ONLY if they apply to you
          </Text>
          <Flex mt={1} color="#000">
            Refer to
            <Box mx={1}>{W4PDFLink}</Box>
            page 2
          </Flex>
        </>
      );
    } else if (currentStep === STEPS.step3 || currentStep === STEPS.step4) {
      noteComponent = (
        <>
          <Text bold color="#000">
            Complete Step 2-4 ONLY if they apply to you
          </Text>
          <Flex mt={1} color="#000">
            Refer to
            <Box mx={1}>{W4PDFLink}</Box>
            page 2
          </Flex>
          <Flex mt={23} column>
            <Text bold color="#000">
              Complete Steps 3–4(b) on Form W-4 for only ONE of these jobs.
            </Text>
            <Flex mt={1} color="#000">
              Leave those steps blank for the other jobs. (Your withholding will
              be most accurate if you complete Steps 3–4(b) on the Form W-4 for
              the highest paying job.)
            </Flex>
          </Flex>
        </>
      );
    }

    return ReactDom.createPortal(
      <Flex
        bg="#FFF7E8"
        mx={-30}
        mt={[-72, -55]}
        py={20}
        mb={48}
        minHeight={92}
      >
        <Flex column style={{ width: '310px', margin: 'auto' }}>
          {noteComponent}
        </Flex>
      </Flex>,
      el,
    );
  };

  const isSigningStep = currentStep === STEPS.step6;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step0 && renderStep0()}
      {currentStep === STEPS.exemptionStatus && renderExemptionStatusStep()}
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
      {currentStep === STEPS.step4 && renderStep4()}
      {currentStep === STEPS.step5 && renderStep5()}
      {currentStep === STEPS.step6 && renderStep6()}
    </StateTaxFormLayout>
  );
}

export default StateW4Form;
